/**
 * chartAdvisor — razona la MEJOR representación visual para un payload de chart,
 * a partir de la forma de los datos (nº de filas/categorías, nº de series,
 * temporal vs categórico, signo), en vez de fiarse del `type` que la tool
 * hardcodea (hoy casi todo `bar`).
 *
 * Función PURA: `chartAdvisor(chart) -> chart'`. Devuelve el payload enriquecido
 * (mismo shape que consume `vizRegistry.resolveChartViz` / `ChartArtifact`):
 *   - puede fijar el `type` óptimo (area/line/bar/pie),
 *   - puede AÑADIR `series[]` para que multi-serie se pinte (barras agrupadas /
 *     multi-línea) cuando hay varias claves numéricas,
 *   - puede HACER UPGRADE a un variant premium cuando los datos encajan
 *     (`income_expense`, `expense_categories`).
 *
 * Conservador: si ya viene un variant premium válido, o no hay datos numéricos,
 * devuelve el chart SIN tocar. Respeta un `viz_hint` opcional del backend
 * (gancho de la Fase 2). Nunca lanza.
 */

const PREMIUM_VARIANTS = new Set([
  'treasury_fan', 'income_expense', 'scenario_compare', 'expense_categories', 'break_even',
  'waterfall', 'composition', 'gauge', 'ratio',
]);

// labelKey temporal por nombre (mes, fecha, trimestre…) o por valor (Ene, Q1, 2026-07).
const TEMPORAL_KEY_RE = /^(mes|fecha|date|periodo|per[ií]odo|d[ií]a|dia|semana|trimestre|a[ñn]o|ano|year|month|week|day)s?$/i;
const TEMPORAL_VAL_RE = /^((ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)([^a-zñ]|$)|q[1-4]\b|t[1-4]\b|\d{4}[-/]\d{1,2})/i;

const SERIES_PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#14b8a6'];

function toNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    if (!/\d/.test(v)) return null; // sin dígitos no es número (evita "y" → 0)
    const n = Number(v.replace(/[^\d.,-]/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function humanize(k) {
  return String(k).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());
}

// ¿el variant premium ya presente trae su data válida? (espejo de resolveChartViz)
function premiumValid(chart) {
  switch (chart.variant) {
    case 'treasury_fan': return Array.isArray(chart.scenarios);
    case 'income_expense': return Array.isArray(chart.series) || Array.isArray(chart.data);
    case 'scenario_compare': return Array.isArray(chart.rows);
    case 'expense_categories': return Array.isArray(chart.categorias) || Array.isArray(chart.data);
    case 'break_even': return chart.costesFijos != null;
    case 'waterfall':
    case 'composition': return Array.isArray(chart.items) || Array.isArray(chart.bars) || Array.isArray(chart.data);
    case 'gauge':
    case 'ratio': return chart.fraction != null || chart.value != null;
    default: return false;
  }
}

function buildSeries(numericKeys) {
  return numericKeys.map((k, i) => ({ key: k, label: humanize(k), color: SERIES_PALETTE[i % SERIES_PALETTE.length] }));
}

function toTreemap(chart, data, labelKey, dataKey) {
  return { ...chart, variant: 'expense_categories', categorias: data.map((r) => ({ categoria: r?.[labelKey], total: toNumber(r?.[dataKey]) ?? 0 })) };
}

export function chartAdvisor(chart) {
  if (!chart || typeof chart !== 'object') return chart;

  // 1) Respeta un variant premium ya decidido y válido.
  if (PREMIUM_VARIANTS.has(chart.variant) && premiumValid(chart)) return chart;

  const data = Array.isArray(chart.data) ? chart.data : null;
  if (!data || data.length === 0) return chart;

  const first = data.find((r) => r && typeof r === 'object');
  if (!first) return chart;
  const keys = Object.keys(first);

  // labelKey: el dado (si existe en los datos), o la 1ª clave no numérica, o la 1ª.
  let labelKey = chart.labelKey && keys.includes(chart.labelKey) ? chart.labelKey : null;
  if (!labelKey) labelKey = keys.find((k) => toNumber(first[k]) == null) || keys[0];

  const numericKeys = keys.filter((k) => k !== labelKey && data.some((r) => toNumber(r?.[k]) != null));
  const nSeries = numericKeys.length;
  if (nSeries === 0) return chart; // nada graficable → no tocar

  const rows = data.length;
  const temporal = TEMPORAL_KEY_RE.test(labelKey) || TEMPORAL_VAL_RE.test(String(first[labelKey] ?? ''));
  const primary = numericKeys[0];
  const withType = (type, extra) => ({ ...chart, labelKey, type, ...extra });
  const multi = nSeries > 1 && !(Array.isArray(chart.series) && chart.series.length) ? { series: buildSeries(numericKeys) } : undefined;

  // 2) viz_hint del backend (Fase 2) o type explícito de la AI — respétalo si es compatible.
  const hint = chart.viz_hint || chart.vizHint;
  if (hint === 'treemap') return toTreemap(chart, data, labelKey, primary);
  if (hint === 'pie') return withType('pie', { dataKey: primary });
  if (hint === 'area' || hint === 'line' || hint === 'bar') return withType(hint, { dataKey: primary, ...multi });

  // 3) Heurística por forma de datos.
  if (temporal) {
    if (nSeries <= 1) return withType('area', { dataKey: primary });
    const lc = numericKeys.map((k) => k.toLowerCase());
    if (lc.some((k) => /ingres/.test(k)) && lc.some((k) => /gast|coste/.test(k))) {
      return { ...chart, variant: 'income_expense', data };
    }
    return withType('line', { dataKey: primary, ...multi });
  }

  // categórico
  if (nSeries <= 1) {
    const allPositive = data.every((r) => (toNumber(r?.[primary]) ?? 0) >= 0);
    if (rows <= 6 && allPositive) return withType('pie', { dataKey: primary });
    if (rows >= 7 && rows <= 25 && /categor|concept|gasto|partida|tipo/i.test(labelKey) && /total|valor|importe|gasto|coste/i.test(primary)) {
      return toTreemap(chart, data, labelKey, primary);
    }
    return withType('bar', { dataKey: primary });
  }

  // categórico multi-serie → barras agrupadas
  return withType('bar', { dataKey: primary, ...multi });
}

export default chartAdvisor;
